import React from 'react';
import { Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { radius, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/ui/Button';
import { setPostLoginRoute } from '@/utils/postLoginRoute';

export interface SignInPerk {
  icon: keyof typeof Ionicons.glyphMap;
  text: string;
}

interface Props {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  message: string;
  /** A short list of what an account gives (optional). */
  perks?: SignInPerk[];
  /** Something under the buttons, e.g. a "Back to browsing" link. */
  footer?: React.ReactNode;
  /** Where to come back to after signing in (the screen that asked); leave out to land on Home. */
  returnTo?: string | null;
}

/**
 * The one "you need an account" panel: an icon, what the screen is for, and the two ways in (Sign In and
 * Create Account), centred in a card of a fixed readable width. Every screen that asks a guest to sign in
 * uses it (the sign-in gate, Profile, Bookings), so the prompt looks and behaves the same everywhere.
 */
export function SignInPanel({ icon, title, message, perks, footer, returnTo = null }: Props) {
  const { colors } = useAppTheme();
  const { tText } = useTranslation();

  return (
    <View style={styles.wrap}>
      <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <View style={[styles.iconCircle, { backgroundColor: colors.accentSoft }]}>
          <Ionicons name={icon} size={32} color={colors.accent} />
        </View>
        <Text style={[type.h2, styles.title, { color: colors.textPrimary }]}>{title}</Text>
        <Text style={[type.body, styles.message, { color: colors.textSecondary }]}>{message}</Text>

        {perks && perks.length > 0 ? (
          <View style={styles.perks}>
            {perks.map((perk) => (
              <View key={perk.text} style={styles.perkRow}>
                <View style={[styles.perkIcon, { backgroundColor: colors.accentSoft }]}>
                  <Ionicons name={perk.icon} size={16} color={colors.accent} />
                </View>
                <Text style={[type.bodyMedium, styles.perkText, { color: colors.textPrimary }]}>{perk.text}</Text>
              </View>
            ))}
          </View>
        ) : null}

        <View style={styles.actions}>
          <Button
            label={tText('Sign In', 'সাইন ইন')}
            icon="log-in-outline"
            iconPosition="right"
            onPress={() => {
              setPostLoginRoute(returnTo);
              router.push('/(auth)/login');
            }}
          />
          <Button label={tText('Create Account', 'অ্যাকাউন্ট তৈরি করুন')} variant="outlined" onPress={() => {
              setPostLoginRoute(returnTo);
              router.push('/(auth)/register');
            }}
          />
        </View>

        {footer}
      </View>
    </View>
  );
}

const styles = ScaledSheet.create({
  // Centred, and never wider than a form should be, however wide the screen is.
  wrap: { width: '100%', maxWidth: 440, alignSelf: 'center' },
  card: { alignItems: 'center', padding: spacing.xl, borderRadius: radius.lg, borderWidth: 1, gap: spacing.md },
  iconCircle: { width: 68, height: 68, borderRadius: 34, alignItems: 'center', justifyContent: 'center' },
  title: { textAlign: 'center' },
  message: { textAlign: 'center' },
  perks: { alignSelf: 'stretch', gap: spacing.sm, marginTop: spacing.xs },
  perkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  perkIcon: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  perkText: { flex: 1 },
  actions: { alignSelf: 'stretch', gap: spacing.sm, marginTop: spacing.sm },
});
